/* Homepage-only enhancements; native controls and Shopify remain authoritative. */
(() => {
  if (window.haiyiHomepage) return;
  let reviewObserver;
  let observedReviews;

  function compactCount(node) {
    const text = node.textContent.trim();
    // Keep native numeric text untouched: Judge.me parses it during init.
    if (!/^\(?[\d,\s]+\)?$/.test(text)) {
      delete node.dataset.haiyiCompact;
      return;
    }
    const count = Number(text.replace(/[^\d]/g, ''));
    if (!Number.isSafeInteger(count) || count < 1000) {
      delete node.dataset.haiyiCompact;
      return;
    }
    if (!node.dataset.haiyiCompact) {
      node.style.setProperty('--haiyi-review-count-size', getComputedStyle(node).fontSize);
    }
    const label = (Math.floor(count / 1000) * 1000).toLocaleString('en-US') + '+';
    node.dataset.haiyiCompact = text.startsWith('(') ? `(${label})` : label;
  }

  function init() {
    const main = document.getElementById('main-content');
    if (!main) return;
    const reviews = main.querySelector('.cc-apps');
    if (reviews !== observedReviews) {
      reviewObserver?.disconnect();
      observedReviews = reviews;
      if (reviews) {
        const update = () => reviews.querySelectorAll('.jdgm-cards-carousel .jdgm-header .jdgm-reviews-count').forEach(compactCount);
        reviewObserver = new MutationObserver(update);
        reviewObserver.observe(reviews, {childList: true, subtree: true, characterData: true});
        update();
      }
    }
  }

  function goTo(target) {
    const details = target.closest('details');
    if (details && !details.open) {
      const disclosure = details.closest('details-disclosure');
      if (typeof disclosure?.open === 'function') disclosure.open();
      else details.open = true;
    }
    requestAnimationFrame(() => {
      const header = document.querySelector('store-header');
      const headerBox = header?.getBoundingClientRect();
      // Reserve the expanded header while native scroll transitions hide/show its rows.
    const fullHeaderHeight = Math.max(
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0,
      (headerBox?.height || 0) + (header ? parseFloat(getComputedStyle(header).marginBottom) || 0 : 0)
    );
    const stickyHeight = headerBox ? Math.max(0, fullHeaderHeight) : 0;
      window.scrollTo({top: window.scrollY + target.getBoundingClientRect().top - stickyHeight - (window.haiyiJumpNav?.extraHeight() || 0) - 18,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      target.focus({preventScroll: true});
    });
  }

  function onClick(event) {
    const link = event.target.closest('a[href^="#"]');
    if (!link || !link.closest('#main-content') || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const hash = link.getAttribute('href');
    if (hash !== '#faq-shipping') return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    event.preventDefault();
    if (target.tagName !== 'SUMMARY') target.setAttribute('tabindex', '-1');
    history.replaceState(null, '', hash);
    goTo(target);
  }

  window.haiyiHomepage = {init};
  document.addEventListener('click', onClick);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:section:unload', event => {
    if (observedReviews && (event.target === observedReviews || event.target.contains(observedReviews))) {
      reviewObserver?.disconnect();
      observedReviews = null;
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();

/* Homepage jump navigation. Native header and modal controls remain authoritative. */
(() => {
  if (window.haiyiJumpNav) return;
  // Compatibility aliases belong to these original instances only. They do not build navigation.
  const legacy = {
    categories: 'collection-list', 'flower-seeds': 'featured_collection_KGEFDM',
    'vegetable-herb-seeds': 'featured_collection_Edxwfm', 'fruit-seeds': 'featured_collection_ff4pTq',
    'planting-guide': 'haiyiguides', reviews: '1766043299a3f07681', faq: 'haiyifaq',
    'featured-collections': 'featured_collection_KGEFDM'
  };
  let registered = [], localEvents;
  const visible = section => section.getClientRects().length > 0 && getComputedStyle(section).visibility !== 'hidden';
  const anchorFor = section => section.querySelector('.section__heading, .haiyi-guides__heading, h2')?.getClientRects().length
    ? section.querySelector('.section__heading, .haiyi-guides__heading, h2') : section;
  function resolve(hash) {
    const id = hash.slice(1);
    return registered.find(section => section.id === id || section.dataset.haiyiLegacy === legacy[id]);
  }
  let source, bar, targets = [], frame = 0, lockedUntil = 0, transitioningUntil = 0;
  let resizeObserver, active = '', scrollGeneration = 0, hoverGuard = null;
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const header = () => document.querySelector('store-header');
  function headerBottom() {
    const h = header();
    return h ? Math.max(0, h.getBoundingClientRect().bottom) : 0;
  }
  function fullHeader() {
    const h = header();
    return h ? Math.max(headerBottom(), h.getBoundingClientRect().height +
      (innerWidth >= 769 && h.classList.contains('is-out') ? h.querySelector('.main-menu__content')?.getBoundingClientRect().height || 0 : 0)) : 0;
  }
  function barHeight() {
    if (innerWidth <= 768 || !bar || !source || !targets.length) return 0;
    if (bar.offsetHeight) return bar.offsetHeight;
    const style = getComputedStyle(bar.firstElementChild);
    return source.querySelector('.haiyi-jump__scroll').getBoundingClientRect().height +
      parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + parseFloat(getComputedStyle(bar).borderBottomWidth);
  }
  function blocked() {
    return document.body.matches('.fixed,.overflow-hidden') || [...document.querySelectorAll('pop-up[open],cart-drawer[open],[role="dialog"][aria-modal="true"]:not([hidden]),.main-menu__disclosure[open],.main-nav details[open]')]
      .some(el => !(el.matches('.main-menu__disclosure') && innerWidth >= 769) && el.getBoundingClientRect().height > 0 && getComputedStyle(el).visibility !== 'hidden' && el.getAttribute('aria-hidden') !== 'true');
  }
  function fade(scroller) {
    scroller.dataset.left = String(scroller.scrollLeft > 2);
    scroller.dataset.right = String(scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft > 2);
  }
  function keepVisible(link) {
    const scroller = link.closest('.haiyi-jump__scroll');
    const area = scroller.getBoundingClientRect(), box = link.getBoundingClientRect();
    const delta = box.left < area.left + 20 ? box.left - area.left - 20 : box.right > area.right - 20 ? box.right - area.right + 20 : 0;
    if (delta) scroller.scrollBy({left: delta, behavior: reduced() ? 'auto' : 'smooth'});
  }
  function update() {
    frame = 0;
    if (!source?.isConnected || !bar) return;
    source.style.setProperty('--haiyi-jump-width', source.closest('.cc-rich-text').getBoundingClientRect().width + 'px');
    const top = headerBottom();
    document.documentElement.style.setProperty('--haiyi-jump-offset', (top + barHeight() + 16) + 'px');
    bar.style.setProperty('--haiyi-jump-top', top + 'px');
    const show = innerWidth >= 769 && source.querySelector('.haiyi-jump__scroll').getBoundingClientRect().bottom <= top && !blocked();
    if (bar.hidden === show) { bar.hidden = !show; bar.inert = !show; }
    let current = '';
    const line = top + bar.offsetHeight + 18;
    for (const target of targets) if (anchorFor(target).getBoundingClientRect().top <= line) current = target.id;
    if (show && scrollY + innerHeight >= document.documentElement.scrollHeight - 2) current = targets.at(-1)?.id || '';
    for (const link of bar.querySelectorAll('a')) {
      const selected = !!current && link.hash === '#' + current;
      if (selected) link.setAttribute('aria-current', 'location'); else link.removeAttribute('aria-current');
      if (selected && current !== active && performance.now() > lockedUntil && show) keepVisible(link);
    }
    active = current;
    source.querySelectorAll('.haiyi-jump__scroll').forEach(fade);
    bar.querySelectorAll('.haiyi-jump__scroll').forEach(fade);
    if (performance.now() < transitioningUntil) schedule();
  }
  function schedule() { if (!frame) frame = requestAnimationFrame(update); }
  function destroy() {
    resizeObserver?.disconnect();
    localEvents?.abort();
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    document.documentElement.style.removeProperty('--haiyi-jump-offset');
    bar?.remove(); bar = null; source = null; targets = []; active = '';
    scrollGeneration++;
  }
  function init() {
    destroy();
    source = document.querySelector('#main-content [data-haiyi-jump]');
    registered = [];
    const missing = [];
    document.querySelectorAll('#main-content [data-haiyi-jump-target]').forEach(marker => {
      const section = marker.closest('.shopify-section');
      if (!section || !visible(section)) return;
      section.dataset.haiyiLegacy = marker.dataset.sectionId.split('__').at(-1);
      registered.push(section);
      // Keep title IDs/ARIA intact; aliases remain valid even when the nav checkbox is off.
      for (const [alias, instance] of Object.entries(legacy)) {
        if (section.dataset.haiyiLegacy !== instance || document.getElementById(alias)) continue;
        const span = document.createElement('span'); span.id = alias;
        span.dataset.haiyiAlias = ''; span.setAttribute('aria-hidden', 'true');
        anchorFor(section).prepend(span);
      }
      if (marker.dataset.enabled !== 'true') return;
      if (marker.dataset.valid !== 'true' || !marker.dataset.label.trim()) { missing.push(marker.dataset.sectionId); return; }
      targets.push(section);
    });
    if (!source) return;
    source.hidden = !targets.length;
    const list = source.querySelector('.haiyi-jump__list'); list.replaceChildren();
    for (const section of targets) {
      const marker = section.querySelector('[data-haiyi-jump-target]');
      const li = document.createElement('li'), link = document.createElement('a');
      link.className = 'haiyi-jump__link'; link.dataset.jump = section.id;
      link.setAttribute('href', '#' + section.id); link.textContent = marker.dataset.label;
      li.append(link); list.append(li);
    }
    const warning = source.querySelector('[data-jump-warning]');
    if (warning) { warning.hidden = !missing.length; warning.textContent = 'Navigation target missing: ' + missing.join(', '); }
    if (!targets.length) return;
    localEvents = new AbortController();
    bar = document.createElement('div');
    bar.className = 'haiyi-jump haiyi-jumpbar'; bar.hidden = true; bar.inert = true;
    const inner = document.createElement('div'); inner.className = 'haiyi-jumpbar__inner';
    const nav = source.querySelector('nav').cloneNode(true);
    nav.setAttribute('aria-label', source.querySelector('nav').getAttribute('aria-label'));
    inner.append(nav); bar.append(inner); document.body.append(bar);
    for (const scroller of [source, bar].flatMap(el => [...el.querySelectorAll('.haiyi-jump__scroll')])) {
      scroller.addEventListener('scroll', () => fade(scroller), {passive: true, signal: localEvents.signal});
      scroller.addEventListener('pointermove', event => {
        if (event.buttons && Math.abs(event.movementX) > Math.abs(event.movementY)) lockedUntil = performance.now() + 1400;
      }, {passive: true, signal: localEvents.signal});
      scroller.addEventListener('touchmove', () => { lockedUntil = performance.now() + 1400; }, {passive: true, signal: localEvents.signal});
      scroller.addEventListener('wheel', () => { lockedUntil = performance.now() + 1400; }, {passive: true, signal: localEvents.signal});
      scroller.addEventListener('focusin', event => { if (event.target.matches('a')) keepVisible(event.target); }, {signal: localEvents.signal});
    }
    resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(source); if (header()) resizeObserver.observe(header());
    targets.forEach(target => resizeObserver.observe(target));
    transitioningUntil = performance.now() + 500;
    schedule();
  }
  function go(hash, historyMode) {
    const target = resolve(hash);
    if (!target) return;
    const anchor = anchorFor(target);
    if (historyMode && location.hash !== hash) history.pushState(null, '', hash);
    const generation = ++scrollGeneration;
    const destination = () => Math.max(0, scrollY + anchor.getBoundingClientRect().top - headerBottom() - (bar?.getBoundingClientRect().height || 0) - 16);
    // A hidden bar has no box. Use the measured row height without revealing it.
    const reserved = barHeight();
    const top = Math.max(0, scrollY + anchor.getBoundingClientRect().top - fullHeader() - reserved - 16);
    scrollTo({top, behavior: reduced() ? 'auto' : 'smooth'});
    if (!anchor.matches('summary')) anchor.tabIndex = -1;
    anchor.focus({preventScroll: true});
    // Native header rows animate for 300ms; correct only after scrolling settles.
    let previous = scrollY, previousHeader = headerBottom(), settled = 0, started = performance.now();
    function finish() {
      if (generation !== scrollGeneration || !target.isConnected) return;
      const actualHeader = headerBottom();
      settled = Math.abs(scrollY - previous) < 0.5 && Math.abs(actualHeader - previousHeader) < 0.5 ? settled + 1 : 0;
      previous = scrollY; previousHeader = actualHeader;
      if (performance.now() - started > 450 && settled > 5) {
        const correction = destination();
        if (Math.abs(scrollY - correction) <= 2 || performance.now() - started > 2200) { schedule(); return; }
        scrollTo({top: correction, behavior: 'instant'}); settled = 0;
      }
      if (performance.now() - started > 2400) { schedule(); return; }
      requestAnimationFrame(finish);
    }
    requestAnimationFrame(finish);
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href^="#"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!resolve(link.hash)) return;
    if (event.detail && link.closest('.haiyi-jumpbar')) hoverGuard = {x: event.clientX, y: event.clientY};
    event.preventDefault(); go(link.hash, true);
  }, true);
  // Header rows can move beneath a stationary pointer during an upward jump.
  document.addEventListener('mouseenter', event => {
    if (hoverGuard && event.target.closest?.('store-header')) event.stopImmediatePropagation();
  }, true);
  window.addEventListener('pointermove', event => {
    if (hoverGuard && (Math.abs(event.clientX - hoverGuard.x) > 2 || Math.abs(event.clientY - hoverGuard.y) > 2)) hoverGuard = null;
  }, {passive: true});
  window.addEventListener('keydown', () => { hoverGuard = null; });
  window.addEventListener('scroll', schedule, {passive: true});
  window.addEventListener('resize', () => { transitioningUntil = performance.now() + 500; schedule(); });
  for (const event of ['popstate', 'hashchange']) window.addEventListener(event, () => go(location.hash, false));
  for (const event of ['wheel', 'touchstart', 'pointerdown']) window.addEventListener(event, () => { scrollGeneration++; }, {passive: true});
  for (const name of ['transitionrun', 'transitionend', 'transitioncancel']) document.addEventListener(name, event => {
    if (event.target.closest('store-header,pop-up,cart-drawer,details-modal,[role="dialog"]')) {
      transitioningUntil = performance.now() + (name === 'transitionrun' ? 500 : 50); schedule();
    }
  });
  new MutationObserver(schedule).observe(document.body, {attributes: true, attributeFilter: ['class']});
  new MutationObserver(records => { if (records.some(r => r.target.closest?.('pop-up,cart-drawer,details-modal,store-header,[role="dialog"]'))) schedule(); }).observe(document.body, {subtree: true, attributes: true, attributeFilter: ['open', 'aria-expanded', 'aria-hidden']});
  for (const event of ['shopify:section:load', 'shopify:section:reorder']) document.addEventListener(event, init);
  document.addEventListener('shopify:section:unload', () => { destroy(); requestAnimationFrame(init); });
  if (window.Shopify?.designMode) new MutationObserver(records => {
    if (records.some(record => record.target.matches?.('#main-content > .shopify-section'))) requestAnimationFrame(init);
  }).observe(document.getElementById('main-content'), {subtree: true, attributes: true, attributeFilter: ['hidden', 'style']});
  window.haiyiJumpNav = {init, extraHeight: barHeight};
  function ready() {
    init();
    const hash = location.hash, generation = scrollGeneration;
    if (!hash) return;
    const afterLoad = () => requestAnimationFrame(() => requestAnimationFrame(() => {
      if (location.hash === hash && scrollGeneration === generation) go(hash, false);
    }));
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, {once: true});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, {once: true}); else ready();
})();
